<script setup>
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import ServiceSection from "./services/ServiceSection.vue";
import {
  getServiceListWithResponseByCode,
  runChainServiceByIndividual,
} from "@/entities/template";
import { useServiceQueueStore } from "@/store/serviceQueue";

import FieldBoolean from "@/features/dynamic-fields/ui/FieldBoolean.vue";
import FieldString from "@/features/dynamic-fields/ui/FieldString.vue";
import FieldDecimal from "@/features/dynamic-fields/ui/FieldDecimal.vue";
import FieldInt from "@/features/dynamic-fields/ui/FieldInt.vue";
import FieldArray from "@/features/dynamic-fields/ui/FieldArray.vue";
import FieldDate from "@/features/dynamic-fields/ui/FieldDate.vue";
import FieldJson from "@/features/dynamic-fields/ui/FieldJson.vue";
import FieldEnum from "@/features/dynamic-fields/ui/FieldEnum.vue";
import FloatingInput from "@/shared/ui/custom/input/FloatingInput.vue";

import { normalizeRuntimeResponse as normalizeRuntimeResponseShared } from "@/shared/serviceResponse";
import InternalServiceList from "./internal_databeses/InternalServiceList.vue";
import InternalServiceDrawer from "./internal_databeses/InternalServiceDrawer.vue";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { Empty } from "@/shared/ui/empty";
import { FormItem, FormLabel } from "@/shared/ui/form";
import CheckInfoBlock from "./CheckInfoBlock.vue";
import {
  fetcherEvents,
  showServiceNotification,
} from "@/shared/lib/centrifuge";
import { generateRequestUuid } from "@/shared/lib/uuid";
import { toast } from "@/shared/store/alertStore";
import { ServiceExecutionStatus } from "@/shared/config/serviceExecutionStatuses";
import { ParticipantRole } from "@/shared/config/participantRoles";
import {
  getCabinetAttorneys,
  getTransactionLinks,
  createCabinetTransactionLink,
  getNotarialItem,
} from "@/entities/transaction";
import PreviewTipTap2 from "@/features/document-editor/ui/PreviewTipTap2.vue";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import { getStatusLabel } from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { Loader2 } from "lucide-vue-next";
import { SvgIcon } from "@/shared/ui/svg-icon";

const props = defineProps({
  uuid: {
    type: [String, Number],
    default: null,
  },
  form: {
    type: Object,
    default: () => ({}),
  },
  execution: {
    type: Object,
    default: () => null,
  },
  activeStepCode: {
    type: String,
    default: "manual_input",
  },
  template: {
    type: Object,
    default: () => ({}),
  },
  selectedParticipant: {
    type: Object,
    default: () => ({}),
  },
  participant_type: {
    type: String,
    default: null,
  },
  inheritanceId: {
    type: String,
    default: "",
  },
  showInheritanceServices: {
    type: Boolean,
    default: false,
  },
  checkResults: {
    type: Object,
    default: null,
  },
  isTestamentAuthor: {
    type: Boolean,
    default: false,
  },
  statementNotFound: {
    type: Boolean,
    default: false,
  },
  existingCaseFound: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "service-response-changed",
  "service-response-error",
  "service-loading-start",
  "service-loading-end",
  "insert:fields",
  "insert:field",
  "show-notary-data",
  "attach-testament",
  "open-testament-document",
  "show-existing-case-info",
  "export-report",
  "attorney-linked",
  "change:tab",
]);

const { t, locale } = useI18n();
const serviceQueue = useServiceQueueStore();

const serviceLoading = ref(false);
const activeService = ref(null);
const selectedService = ref(null);
const serviceResponseData = ref(null);
const dynamicForm = ref({});
const searching = ref(false);
const searchCompleted = ref(false);
const pendingServiceCode = ref(null);
const pendingRequestUuid = ref(null);
const manualInputFields = ref({});

const hasManualInputValues = computed(() =>
  Object.values(manualInputFields.value || {}).some(
    (v) => v !== null && v !== undefined && String(v).trim() !== ""
  )
);

const showServices = computed(
  () =>
    props.selectedParticipant?.participant_type_code ===
    ParticipantRole.DOVERENNYI
);

const attorneyDrawer = reactive({
  visible: false,
  service: null,
  initialTab: "search",
});

function openAttorneyDrawer(service, initialTab = "search") {
  attorneyDrawer.service = service;
  attorneyDrawer.initialTab = initialTab;
  attorneyDrawer.visible = true;
}

const ATTORNEY_SERVICE = {
  id: "attorney_check",
  title: "Проверка доверенности",
};

const attorneyCheck = reactive({
  state: "idle",
  results: [],
  links: [],
  selectedId: null,
  attaching: false,
});

const emptyDescription = computed(() => {
  const iin = props.selectedParticipant?.identifier;
  const tail = t(
    "Загрузите файл доверенности на вкладке «Вложения», чтобы продолжить."
  );
  return iin ? `${t("ИИН")}: ${iin}. ${tail}` : tail;
});

const attachedHeading = computed(() => {
  const n = attorneyCheck.links.length;
  if (n === 1) return t("Прикреплена 1 доверенность");
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${t("Прикреплены")} ${n} ${t("доверенности")}`;
  }
  return `${t("Прикреплено")} ${n} ${t("доверенностей")}`;
});

const attorneyCheckKey = computed(() => {
  if (!showServices.value) return null;
  if (props.activeStepCode !== "internal_databases") return null;
  const id = props.form?.id;
  const identifier = props.selectedParticipant?.identifier;
  if (!id || !identifier) return null;
  return `${id}:${identifier}`;
});

watch(
  attorneyCheckKey,
  (key) => {
    if (!key) return;
    runAttorneyCheck();
  },
  { immediate: true }
);

async function runAttorneyCheck() {
  if (!showServices.value) return;
  if (props.activeStepCode !== "internal_databases") return;
  const transactionId = props.form?.id;
  const identifier = props.selectedParticipant?.identifier;
  if (!transactionId || !identifier) return;

  attorneyCheck.state = "loading";
  attorneyCheck.selectedId = null;

  try {
    const [linksRes, attorneysRes] = await Promise.all([
      getTransactionLinks(transactionId),
      getCabinetAttorneys({ identifier, page: 1, size: 20 }),
    ]);

    const linksList =
      (linksRes?.success && (linksRes?.payload?.items || linksRes?.payload)) ||
      [];
    attorneyCheck.links = Array.isArray(linksList) ? linksList : [];

    const items = (attorneysRes?.success && attorneysRes?.payload?.items) || [];
    const linkedIds = new Set(
      attorneyCheck.links.map((l) => l.target_transaction_id).filter(Boolean)
    );
    attorneyCheck.results = items.filter((it) => !linkedIds.has(it.id));

    if (attorneyCheck.links.length > 0) {
      attorneyCheck.state = "attached";
    } else if (attorneyCheck.results.length > 0) {
      attorneyCheck.state = "found";
    } else {
      attorneyCheck.state = "empty";
    }
  } catch (e) {
    console.error("runAttorneyCheck error", e);
    attorneyCheck.state = "error";
  }
}

function selectAttorney(item) {
  attorneyCheck.selectedId =
    attorneyCheck.selectedId === item.id ? null : item.id;
}

async function attachSelectedAttorney() {
  const transactionId = props.form?.id;
  if (!transactionId || !attorneyCheck.selectedId || attorneyCheck.attaching) {
    return;
  }
  const item = attorneyCheck.results.find(
    (r) => r.id === attorneyCheck.selectedId
  );
  if (!item) return;

  attorneyCheck.attaching = true;
  try {
    const body = {
      target_transaction_id: item.id,
      note: `Прикреплено из сервиса: ${ATTORNEY_SERVICE.title}. № действия: ${
        item.number || ""
      }`,
    };
    const data = await createCabinetTransactionLink(transactionId, body);
    if (data?.success || data?.ok) {
      toast.success(t("Доверенность прикреплена"));
      emit("attorney-linked");
      await runAttorneyCheck();
    } else {
      toast.error(t("Не удалось прикрепить доверенность"));
    }
  } catch (e) {
    console.error("attachSelectedAttorney error", e);
    toast.error(t("Не удалось прикрепить доверенность"));
  } finally {
    attorneyCheck.attaching = false;
  }
}

function onUploadAttorney() {
  emit("change:tab", "attachments");
}

const actionsOpen = ref(false);

function closeActions() {
  actionsOpen.value = false;
}

function onRecheck() {
  closeActions();
  runAttorneyCheck();
}

function onOpenManualSearch() {
  closeActions();
  openAttorneyDrawer(ATTORNEY_SERVICE);
}

function onOpenManageLinks() {
  closeActions();
  openAttorneyDrawer(ATTORNEY_SERVICE, "links");
}

function onUploadFromMenu() {
  closeActions();
  onUploadAttorney();
}

const primaryAction = computed(() => {
  switch (attorneyCheck.state) {
    case "attached":
    case "empty":
      return {
        label: t("Загрузить"),
        icon: "huge-upload",
        handler: onUploadAttorney,
      };
    case "found":
      return {
        label: t("Прикрепить"),
        handler: attachSelectedAttorney,
        disabled: !attorneyCheck.selectedId || attorneyCheck.attaching,
        loading: attorneyCheck.attaching,
      };
    case "error":
      return {
        label: t("Повторить"),
        handler: runAttorneyCheck,
      };
    default:
      return null;
  }
});

const secondaryActions = computed(() => {
  const search = {
    label: t("Поиск по другим данным"),
    handler: onOpenManualSearch,
  };
  const recheck = {
    label: t("Проверить ещё раз"),
    handler: onRecheck,
  };
  const upload = {
    label: t("Загрузить"),
    icon: "huge-upload",
    handler: onUploadFromMenu,
  };
  const manage = {
    label: t("Управление связями"),
    handler: onOpenManageLinks,
  };

  switch (attorneyCheck.state) {
    case "attached":
      return [manage, search];
    case "found":
      return [search, upload];
    case "empty":
      return [recheck, search];
    case "error":
      return [search, upload];
    default:
      return [];
  }
});

const previewRef = ref(null);
const previewJson = ref(null);
const previewLang = ref("ru");
const previewLoadingId = ref(null);

async function previewAttorney(transactionId) {
  if (!transactionId || previewLoadingId.value) return;
  previewLoadingId.value = transactionId;
  try {
    const data = await getNotarialItem(transactionId);
    if (data?.success && data.payload) {
      const lang = (data.payload?.languages || ["ru"])[0] || "ru";
      const key =
        lang === "kz" ? "content_kz" : lang === "en" ? "content_en" : "content";
      const json = data.payload?.[key];
      if (!json) {
        toast.error(t("Документ доверенности недоступен для просмотра"));
        return;
      }
      previewLang.value = lang;
      previewJson.value = json;
      await nextTick();
      previewRef.value?.open({ item: json });
    } else {
      toast.error(t("Не удалось загрузить документ"));
    }
  } catch (e) {
    console.error("previewAttorney error", e);
    toast.error(t("Не удалось загрузить документ"));
  } finally {
    previewLoadingId.value = null;
  }
}

function attorneyTitle(item) {
  return (
    item?.template_name ||
    item?.info?.name ||
    item?.info?.template_name ||
    item?.template?.code ||
    t("Доверенность")
  );
}

function attorneyNotaryName(item) {
  const info =
    item?.notary_info || item?.info?.notary_info || item?.notary_data;
  if (!info) return "";
  if (info.full_name) return info.full_name;
  if (info.fio) return info.fio;
  return [info.last_name, info.first_name, info.middle_name]
    .filter(Boolean)
    .join(" ");
}

function attorneyParticipants(item) {
  const list = item?.participants || item?.info?.participants;
  if (!Array.isArray(list) || !list.length) return "";
  return list
    .map((p) => {
      if (p?.first_name) {
        return [p.first_name, p.last_name].filter(Boolean).join(" ");
      }
      return p?.params?.company_name || p?.identifier || "";
    })
    .filter(Boolean)
    .join(", ");
}

function attorneyDate(item) {
  const raw =
    item?.registration_date ||
    item?.info?.registration_date ||
    item?.created_at;
  if (!raw) return "";
  const date = new Date(raw);
  return (
    date.toLocaleDateString("ru-RU") + " " + date.toLocaleTimeString("ru-RU")
  );
}

function attorneyStatusLabel(status) {
  if (!status) return "";
  return getStatusLabel(status) || status;
}

let unsubscribeWs = null;
let requestStartedAt = 0;
const MIN_SKELETON_MS = 1000;

const dynamicRequestFields = computed(() => {
  const request = activeService.value?.request;
  if (!request?.properties) return [];

  const required = request.required || [];

  return Object.entries(request.properties)
    .filter(([key]) => required.includes(key))
    .map(([key, schema]) => ({
      key,
      title: schema.title ?? key,
      type: schema.type?.toLowerCase(),
      enums: schema.enum,
      required: true,
      schema,
    }));
});

const currentSteps = computed(() => {
  return props.execution?.steps_entity_types[props.participant_type];
});

const currentStep = computed(() => {
  const ex = props.execution;
  if (!ex || !activeService.value?.code) return null;
  return (
    currentSteps.value?.find((s) => s.code === activeService.value.code) || null
  );
});

const queueStepStatus = computed(() => {
  const pUuid = props.selectedParticipant?.uuid;
  const code = activeService.value?.code || props.activeStepCode;
  if (!pUuid || !code) return null;
  return serviceQueue.getStepStatus(pUuid, code);
});

const isQueueRunning = computed(() => queueStepStatus.value === "running");

const isSearching = computed(() => searching.value || isQueueRunning.value);

watch(
  () => props.activeStepCode,
  (code) => {
    if (!code) {
      activeService.value = null;
      selectedService.value = null;
      dynamicForm.value = {};
      searchCompleted.value = false;
      return;
    }

    loadServiceByCode(code);
  },
  { immediate: true }
);

watch(
  activeService,
  (service) => {
    if (service?.code === "manual_input") {
      initManualInputFields();
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedParticipant,
  (participant) => {
    if (participant?.identifier) {
      dynamicForm.value["person_identifier"] = participant.identifier;
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedParticipant?.uuid,
  (newUuid, oldUuid) => {
    if (!newUuid || newUuid === oldUuid) return;
    const code = props.activeStepCode;
    if (!code || code === "manual_input" || code === "show-check-info") return;
    const queueData = serviceQueue.getStepData(newUuid, code);
    const queueStatus = serviceQueue.getStepStatus(newUuid, code);
    const savedData = props.selectedParticipant?.service_fields?.[code];
    if (queueData && queueStatus === "success") {
      serviceResponseData.value = queueData;
      searchCompleted.value = true;
    } else if (savedData && Object.keys(savedData).length > 0) {
      serviceResponseData.value = savedData;
      searchCompleted.value = true;
    } else {
      serviceResponseData.value = null;
      searchCompleted.value = false;
    }
  }
);

watch(
  () => props.form?.fields,
  (fields) => {
    if (!fields) return;
    if (
      props.activeStepCode === "manual_input" ||
      activeService.value?.code === "manual_input"
    ) {
      initManualInputFields();
    }
  },
  { immediate: true }
);

watch(
  manualInputFields,
  (val) => {
    if (!props.form || !val) return;
    if (!props.form.fields) {
      props.form.fields = {};
    }

    Object.entries(val).forEach(([key, value]) => {
      const existing = props.form.fields[key];
      if (existing && typeof existing === "object") {
        existing.value = value;
      } else {
        props.form.fields[key] = { value };
      }
    });
  },
  { deep: true }
);

async function loadServiceByCode(code) {
  serviceResponseData.value = null;
  searchCompleted.value = false;
  searching.value = false;
  pendingServiceCode.value = null;
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  if (code === "manual_input") {
    activeService.value = { code: "manual_input", name: "Ручной ввод" };
    initManualInputFields();
  } else if (code === "show-check-info") {
    activeService.value = {
      code: "show-check-info",
      name: "Информация проверки",
    };
  } else {
    try {
      serviceLoading.value = true;
      const data = await getServiceListWithResponseByCode(code);
      if (data?.success && data.payload) {
        activeService.value = data.payload;
        dynamicForm.value = initDynamicForm(
          data.payload.request?.properties || {}
        );
        searchCompleted.value = false;

        selectedService.value = {
          name: data.payload.name,
          code: data.payload.code,
          response: data.payload.response,
        };
      } else {
        activeService.value = null;
        selectedService.value = null;
        dynamicForm.value = {};
      }
    } catch (e) {
      console.error("loadServiceByCode error:", e);
      activeService.value = null;
      selectedService.value = null;
      dynamicForm.value = {};
    } finally {
      serviceLoading.value = false;
    }

    if (
      props.selectedParticipant?.identifier &&
      checkHasKeyDynamicForm("person_identifier")
    ) {
      dynamicForm.value["person_identifier"] =
        props.selectedParticipant.identifier;
    }
    if (
      props.selectedParticipant?.identifier &&
      checkHasKeyDynamicForm("business_identifier")
    ) {
      dynamicForm.value["business_identifier"] =
        props.selectedParticipant?.params?.company_bin;
    }

    const pUuid = props.selectedParticipant?.uuid;
    if (pUuid) {
      const queueData = serviceQueue.getStepData(pUuid, code);
      const queueStatus = serviceQueue.getStepStatus(pUuid, code);
      if (queueData && queueStatus === "success") {
        serviceResponseData.value = queueData;
        searchCompleted.value = true;
      } else {
        const savedData = props.selectedParticipant?.service_fields?.[code];
        if (savedData && Object.keys(savedData).length > 0) {
          serviceResponseData.value = savedData;
          searchCompleted.value = true;
        }
      }
    }
  }
}

function initDynamicForm(properties = {}) {
  return Object.fromEntries(
    Object.entries(properties).map(([key, schema]) => [
      key,
      schema.default ?? (schema.type === "boolean" ? false : ""),
    ])
  );
}

function getFieldComponent(field) {
  if (field.enums?.length) return FieldEnum;

  const map = {
    boolean: FieldBoolean,
    string: FieldString,
    number: FieldDecimal,
    integer: FieldInt,
    array: FieldArray,
    datetime: FieldDate,
    object: FieldJson,
  };

  return map[field.type] || FieldString;
}

function prepareServicePayload() {
  return {
    ...dynamicForm.value,
    identifier: props.selectedParticipant?.identifier || "",
  };
}

async function validateForms() {
  return true;
}

function loadingPayload() {
  return {
    service_code: activeService.value?.code,
    participant_type_code: props.selectedParticipant?.participant_type_code,
  };
}

async function getServiceResponse() {
  if (!(await validateForms())) return;

  searching.value = true;
  searchCompleted.value = true;

  const startPayload = loadingPayload();
  requestStartedAt = Date.now();
  emit("service-loading-start", startPayload);

  try {
    const execution = props.execution;
    const step = currentStep.value;

    if (!execution || !step) {
      emit("service-loading-end", startPayload);
      return;
    }

    // Подписываемся ДО запуска — чтобы не пропустить быстрый WS-ответ (гонка).
    const clientRequestUuid = generateRequestUuid();
    pendingServiceCode.value = activeService.value?.code;
    pendingRequestUuid.value = clientRequestUuid;
    unsubscribeWs = fetcherEvents.on(handleWsResponse);

    const result = await runChainServiceByIndividual(
      execution.uuid,
      step.uuid,
      props.uuid,
      { ...prepareServicePayload(), request_uuid: clientRequestUuid }
    );

    if (result?.status === 403 || result?.payload?.has_access === false) {
      pendingServiceCode.value = null;
      pendingRequestUuid.value = null;
      if (unsubscribeWs) {
        unsubscribeWs();
        unsubscribeWs = null;
      }
      toast.error(t("Доверенность в системе ЕНИС не зарегистрирована"), {
        duration: 5000,
      });
      emit("service-response-error");
      emit("service-loading-end", startPayload);
      searching.value = false;
      return;
    }

    const chainRunData = result?.payload?.chain_run?.payload || result?.payload;

    if (result?.success && chainRunData?.run) {
      const attorneyLinked = !!(
        result?.payload?.attorney_transaction_id ||
        (Array.isArray(result?.payload?.links) &&
          result.payload.links.length > 0)
      );
      if (attorneyLinked) {
        emit("attorney-linked");
      }
      // НЕ снимаем searching — спиннер крутится пока ждём WS
      return;
    }

    pendingServiceCode.value = null;
    pendingRequestUuid.value = null;
    if (unsubscribeWs) {
      unsubscribeWs();
      unsubscribeWs = null;
    }
    emit("service-response-error");
    emit("service-loading-end", startPayload);
  } catch (e) {
    console.error("getServiceResponse error:", e);
    pendingServiceCode.value = null;
    pendingRequestUuid.value = null;
    if (unsubscribeWs) {
      unsubscribeWs();
      unsubscribeWs = null;
    }
    emit("service-response-error");
    emit("service-loading-end", startPayload);
  } finally {
    if (!pendingServiceCode.value) {
      setTimeout(() => (searching.value = false), 800);
    }
  }
}

function initManualInputFields() {
  const source = props.form?.fields;
  if (source && Object.keys(source).length) {
    manualInputFields.value = Object.fromEntries(
      Object.entries(source).map(([key, field]) => [
        key,
        (typeof field === "object" && field !== null ? field.value : field) ||
          "",
      ])
    );
  } else {
    manualInputFields.value = {};
  }
}

function insertAllManualFieldsToDocument() {
  const entries = Object.entries(manualInputFields.value || {});
  if (!entries.length) return;
  const fields = entries.map(([key, value]) => ({
    key,
    value,
    label: props.form?.fields?.[key]?.name || key,
    isCustom: true,
  }));
  emit("insert:fields", fields);
}

function checkHasKeyDynamicForm(key) {
  return Object.keys(dynamicForm.value).includes(key);
}

function normalizeRuntimeResponse(payload) {
  return normalizeRuntimeResponseShared(payload, {
    locale: locale.value || "ru",
    participantTypeCode: props.selectedParticipant?.participant_type_code,
    serviceCode: activeService.value?.code,
  });
}

onBeforeUnmount(() => {
  if (pendingServiceCode.value) {
    emit("service-loading-end", {
      service_code: pendingServiceCode.value,
      participant_type_code: props.selectedParticipant?.participant_type_code,
    });
  }
  pendingServiceCode.value = null;
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }
});

function handleArrayItemSelected(payload) {
  if (payload?.fields.length) {
    emit("insert:fields", payload.fields);
  }
}

function handleWsResponse(data) {
  if (!pendingServiceCode.value) {
    console.error(
      "[WS] ПРОПУСК: pendingServiceCode пустой — ответ уже обработан или не ожидался"
    );
    return;
  }
  if (data?.type !== "FETCHER") {
    console.warn(
      "[WS] ПРОПУСК: тип сообщения не FETCHER, получен:",
      data?.type
    );
    return;
  }

  const wsPayload = data?.payload;
  if (!wsPayload || wsPayload.service_code !== pendingServiceCode.value) {
    console.error(
      "[WS] ПРОПУСК: код сервиса не совпадает. Пришёл:",
      wsPayload?.service_code,
      "| Ожидался:",
      pendingServiceCode.value
    );
    return;
  }
  if (
    pendingRequestUuid.value &&
    data.request_uuid !== pendingRequestUuid.value
  ) {
    console.error(
      "[WS] ПРОПУСК: uuid запроса не совпадает. Пришёл:",
      data.request_uuid,
      "| Ожидался:",
      pendingRequestUuid.value
    );
    return;
  }

  // PENDING — ещё обрабатывается, продолжаем ждать
  if (wsPayload.status === ServiceExecutionStatus.PENDING) {
    console.warn("[WS] PENDING. service:", wsPayload.service_code);
    return;
  }

  // NOTIFICATION — ожидание подтверждения клиента (КДП), loading продолжается
  if (wsPayload.status === ServiceExecutionStatus.NOTIFICATION) {
    showServiceNotification(wsPayload);
    return;
  }

  const finishedServiceCode = pendingServiceCode.value;
  pendingServiceCode.value = null;
  pendingRequestUuid.value = null;

  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  const endPayload = {
    service_code: finishedServiceCode,
    participant_type_code: props.selectedParticipant?.participant_type_code,
  };

  const elapsed = Date.now() - (requestStartedAt || Date.now());
  const remaining = Math.max(0, MIN_SKELETON_MS - elapsed);

  setTimeout(() => {
    if (
      wsPayload.status === ServiceExecutionStatus.FINISHED &&
      wsPayload.data &&
      Object.keys(wsPayload.data).length > 0
    ) {
      serviceResponseData.value = wsPayload.data;
      emit("service-response-changed", wsPayload.data);

      const fields = normalizeRuntimeResponse(wsPayload.data);
      if (fields.length) {
        emit("insert:fields", fields);
      }

      toast.success(t("Данные получены"), {
        description: new Date().toLocaleTimeString(),
        duration: 3000,
      });
    } else {
      emit("service-response-error");

      const errorMsg =
        wsPayload?.error_description ||
        t("Не удалось получить данные от сервиса");
      toast.error(errorMsg, {
        duration: 5000,
      });
    }

    emit("service-loading-end", endPayload);
    searching.value = false;
  }, remaining);
}
</script>

<template>
  <div class="w-full h-full flex flex-col">
    <ServiceSection
      v-if="activeStepCode === 'manual_input'"
      title="Ручной ввод"
      :selectedService="activeService"
      :show-action="true"
      :type="'show-service-response-viewer'"
      :selectedParticipant="selectedParticipant"
    >
      <template #action>
        <div>
          <div class="flex flex-col w-full gap-2.5">
            <div
              v-for="(value, key) in manualInputFields"
              :key="key"
              class="w-full"
            >
              <FloatingInput
                v-model="manualInputFields[key]"
                :key-field="key"
                :label="form?.fields?.[key]?.name || 'Введите значение'"
                class="w-full"
                @insert:field="$emit('insert:field', $event)"
              />
            </div>

            <Button
              v-if="Object.keys(manualInputFields).length"
              variant="outline"
              class="w-full mt-2 flex items-center justify-center"
              :disabled="!hasManualInputValues"
              @click="insertAllManualFieldsToDocument"
            >
              <SvgIcon name="huge-plus" class="w-3.5 h-3.5" />
              {{ $t("Добавить в документ") }}
            </Button>
          </div>
        </div>
      </template>
    </ServiceSection>

    <div
      v-else-if="activeStepCode === 'internal_databases' && showServices"
      class="attorney-panel"
    >
      <header class="attorney-panel__header">
        <h2
          v-if="
            attorneyCheck.state === 'attached' ||
            attorneyCheck.state === 'found'
          "
          class="attorney-panel__title"
        >
          {{ $t("Внутренние базы") }}
        </h2>
        <div
          v-if="attorneyCheck.state === 'found'"
          class="attorney-found__hint"
        >
          <div>{{ $t("Выберите подходящую и нажмите «Прикрепить».") }}</div>
        </div>

        <div
          v-if="attorneyCheck.state === 'attached'"
          class="attorney-found__hint"
        >
          {{ attachedHeading }}
        </div>
      </header>

      <div class="attorney-panel__body">
        <div
          v-if="
            attorneyCheck.state === 'loading' || attorneyCheck.state === 'idle'
          "
          class="attorney-loading"
        >
          <Loader2 class="w-9 h-9 animate-spin text-primary" />
          <div class="attorney-loading__title">
            {{ $t("Проверяем реестр доверенностей") }}
          </div>
          <div
            v-if="selectedParticipant?.identifier"
            class="attorney-loading__sub"
          >
            {{ $t("ИИН") }}: {{ selectedParticipant.identifier }}
          </div>
        </div>

        <div
          v-else-if="attorneyCheck.state === 'attached'"
          class="attorney-attached"
        >
          <div
            v-for="link in attorneyCheck.links"
            :key="link.id"
            class="attached-card"
          >
            <div class="attached-card__title">
              <!--              <SvgIcon-->
              <!--                name="sli-check-circle"-->
              <!--                class="w-5 h-5 text-green-500"-->
              <!--                style="fill: none"-->
              <!--              />-->
              {{
                link?.info?.name || link?.template_name || $t("Доверенность")
              }}
            </div>
            <div class="attached-card__rows">
              <div v-if="link.number || link.info?.number" class="row">
                <div class="row__label">
                  {{ $t("№ нотариального действия") }}
                </div>
                <div class="row__value">
                  {{ link.number || link.info?.number }}
                </div>
              </div>
              <div v-if="link.info?.bso_number || link.bso_number" class="row">
                <div class="row__label">{{ $t("№ БСО") }}</div>
                <div class="row__value">
                  {{ link.info?.bso_number || link.bso_number }}
                </div>
              </div>
              <div
                v-if="link.info?.registry_number || link.registry_number"
                class="row"
              >
                <div class="row__label">{{ $t("Реестровый №") }}</div>
                <div class="row__value">
                  {{ link.info?.registry_number || link.registry_number }}
                </div>
              </div>
              <div v-if="attorneyDate(link)" class="row">
                <div class="row__label">{{ $t("Дата") }}</div>
                <div class="row__value">{{ attorneyDate(link) }}</div>
              </div>
              <div
                v-if="
                  link.info?.template_category_name ||
                  link.template_category_name
                "
                class="row"
              >
                <div class="row__label">{{ $t("Категория") }}</div>
                <div class="row__value">
                  {{
                    link.info?.template_category_name ||
                    link.template_category_name
                  }}
                </div>
              </div>
              <div
                v-if="link.info?.template_name || link.template_name"
                class="row"
              >
                <div class="row__label">{{ $t("Шаблон") }}</div>
                <div class="row__value">
                  {{ link.info?.template_name || link.template_name }}
                </div>
              </div>
              <div v-if="attorneyNotaryName(link.info || link)" class="row">
                <div class="row__label">{{ $t("Нотариус") }}</div>
                <div class="row__value">
                  {{ attorneyNotaryName(link.info || link) }}
                </div>
              </div>
              <div
                v-if="link.info?.notary_info?.region || link.notary_region"
                class="row"
              >
                <div class="row__label">{{ $t("Регион") }}</div>
                <div class="row__value">
                  {{ link.info?.notary_info?.region || link.notary_region }}
                </div>
              </div>
              <div v-if="attorneyParticipants(link.info || link)" class="row">
                <div class="row__label">{{ $t("Участники") }}</div>
                <div class="row__value">
                  {{ attorneyParticipants(link.info || link) }}
                </div>
              </div>
              <div class="row">
                <div class="row__label">{{ $t("Статус") }}</div>
                <div class="row__value row__value--status">
                  <StatusDot :code="link.info?.status || link.status" />
                  {{
                    attorneyStatusLabel(link.info?.status || link.status) || "—"
                  }}
                </div>
              </div>
              <div v-if="link.note" class="row">
                <div class="row__label">{{ $t("Примечание") }}</div>
                <div class="row__value">{{ link.note }}</div>
              </div>
              <div v-if="link.created_at" class="row">
                <div class="row__label">{{ $t("Прикреплено") }}</div>
                <div class="row__value">
                  {{ attorneyDate({ registration_date: link.created_at }) }}
                </div>
              </div>
            </div>
            <button
              v-if="link.target_transaction_id"
              type="button"
              class="preview-btn"
              :title="$t('Предпросмотр доверенности')"
              :disabled="previewLoadingId === link.target_transaction_id"
              @click.stop="previewAttorney(link.target_transaction_id)"
            >
              <Loader2
                v-if="previewLoadingId === link.target_transaction_id"
                class="w-4 h-4 animate-spin"
              />
              <SvgIcon v-else name="huge-eye" class="w-4 h-4" />
            </button>
          </div>
        </div>

        <div v-else-if="attorneyCheck.state === 'found'" class="attorney-found">
          <div class="attorney-found__list">
            <div
              v-for="item in attorneyCheck.results"
              :key="item.id"
              class="attorney-card"
              :class="{
                'is-selected': attorneyCheck.selectedId === item.id,
              }"
              @click="selectAttorney(item)"
            >
              <div class="attorney-card__header">
                <div class="attorney-card__title">
                  {{ attorneyTitle(item) }}
                </div>
                <div class="attorney-card__header-actions">
                  <button
                    type="button"
                    class="preview-btn"
                    :title="$t('Предпросмотр доверенности')"
                    :disabled="previewLoadingId === item.id"
                    @click.stop="previewAttorney(item.id)"
                  >
                    <Loader2
                      v-if="previewLoadingId === item.id"
                      class="w-4 h-4 animate-spin"
                    />
                    <SvgIcon v-else name="huge-eye" class="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div class="attorney-card__rows">
                <div class="row">
                  <div class="row__label">
                    {{ $t("№ нотариального действия") }}
                  </div>
                  <div class="row__value">{{ item.number || "—" }}</div>
                </div>
                <div v-if="attorneyDate(item)" class="row">
                  <div class="row__label">{{ $t("Дата") }}</div>
                  <div class="row__value">{{ attorneyDate(item) }}</div>
                </div>
                <div v-if="attorneyNotaryName(item)" class="row">
                  <div class="row__label">{{ $t("Нотариус") }}</div>
                  <div class="row__value">
                    {{ attorneyNotaryName(item) }}
                  </div>
                </div>
                <div v-if="attorneyParticipants(item)" class="row">
                  <div class="row__label">{{ $t("Участники") }}</div>
                  <div class="row__value">
                    {{ attorneyParticipants(item) }}
                  </div>
                </div>
                <div v-if="item.status" class="row">
                  <div class="row__label">{{ $t("Статус") }}</div>
                  <div class="row__value row__value--status">
                    <StatusDot :code="item.status" />
                    {{ attorneyStatusLabel(item.status) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Empty
          v-else-if="attorneyCheck.state === 'empty'"
          card
          icon="huge-search-file"
          :title="$t('Доверенность не найдена в реестре')"
          :description="emptyDescription"
        >
          <template #action>
            <div class="attorney-panel__inline-actions">
              <Popover v-model:open="actionsOpen">
                <PopoverTrigger as-child>
                  <Button variant="outline" class="w-full">
                    <SvgIcon name="huge-more" class="w-4 h-4 mr-1.5" />
                    {{ $t("Действия") }}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" :side-offset="8" class="w-72">
                  <div class="flex flex-col gap-2">
                    <Button
                      v-for="(action, idx) in secondaryActions"
                      :key="idx"
                      variant="outline"
                      class="w-full justify-start"
                      @click="action.handler"
                    >
                      <SvgIcon
                        v-if="action.icon"
                        :name="action.icon"
                        class="w-4 h-4 mr-1.5"
                      />
                      {{ action.label }}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                v-if="primaryAction"
                class="w-full"
                :disabled="!!primaryAction.disabled"
                @click="primaryAction.handler"
              >
                <SvgIcon
                  v-if="primaryAction.icon"
                  :name="primaryAction.icon"
                  class="w-4 h-4 mr-1.5"
                />
                {{ primaryAction.label }}
              </Button>
            </div>
          </template>
        </Empty>

        <Empty
          v-else-if="attorneyCheck.state === 'error'"
          card
          icon="huge-warning"
          :title="$t('Не удалось проверить реестр')"
          :description="$t('Попробуйте ещё раз или загрузите файл вручную.')"
        >
          <template #action>
            <div class="attorney-panel__inline-actions">
              <Popover v-model:open="actionsOpen">
                <PopoverTrigger as-child>
                  <Button variant="outline" class="w-full">
                    <SvgIcon name="huge-more" class="w-4 h-4 mr-1.5" />
                    {{ $t("Действия") }}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" :side-offset="8" class="w-72">
                  <div class="flex flex-col gap-2">
                    <Button
                      v-for="(action, idx) in secondaryActions"
                      :key="idx"
                      variant="outline"
                      class="w-full justify-start"
                      @click="action.handler"
                    >
                      <SvgIcon
                        v-if="action.icon"
                        :name="action.icon"
                        class="w-4 h-4 mr-1.5"
                      />
                      {{ action.label }}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                v-if="primaryAction"
                class="w-full"
                :disabled="!!primaryAction.disabled"
                @click="primaryAction.handler"
              >
                {{ primaryAction.label }}
              </Button>
            </div>
          </template>
        </Empty>
      </div>

      <footer
        v-if="
          attorneyCheck.state === 'attached' || attorneyCheck.state === 'found'
        "
        class="attorney-panel__footer"
      >
        <Popover v-model:open="actionsOpen">
          <PopoverTrigger as-child>
            <Button variant="outline" class="w-full">
              <SvgIcon name="huge-more" class="w-4 h-4 mr-1.5" />
              {{ $t("Действия") }}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" :side-offset="8" class="w-72">
            <div class="flex flex-col gap-2">
              <Button
                v-for="(action, idx) in secondaryActions"
                :key="idx"
                variant="outline"
                class="w-full justify-start"
                @click="action.handler"
              >
                <SvgIcon
                  v-if="action.icon"
                  :name="action.icon"
                  class="w-4 h-4 mr-1.5"
                />
                {{ action.label }}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          v-if="primaryAction"
          class="w-full"
          :disabled="!!primaryAction.disabled"
          @click="primaryAction.handler"
        >
          <Loader2
            v-if="primaryAction.loading"
            class="w-4 h-4 animate-spin mr-1.5"
          />
          <SvgIcon
            v-else-if="primaryAction.icon"
            :name="primaryAction.icon"
            class="w-4 h-4 mr-1.5"
          />
          {{ primaryAction.label }}
        </Button>
      </footer>

      <InternalServiceDrawer
        v-if="attorneyDrawer.visible"
        :service="attorneyDrawer.service"
        :selectedParticipant="selectedParticipant"
        :item="form"
        :initialTab="attorneyDrawer.initialTab"
        @close="attorneyDrawer.visible = false"
        @upload-requested="onUploadAttorney"
      />
    </div>

    <ServiceSection
      v-else-if="activeStepCode === 'internal_databases'"
      title="Внутренние базы"
      :selectedService="activeService"
      :show-action="true"
      :type="'show-service-response-viewer'"
      :selectedParticipant="selectedParticipant"
    >
      <template #action>
        <div class="flex flex-col gap-6">
          <InternalServiceList
            :selectedParticipant="selectedParticipant"
            :selectedService="activeService"
            :form="form"
            :inheritanceId="inheritanceId"
            :showInheritanceServices="showInheritanceServices"
          />
        </div>
      </template>
    </ServiceSection>

    <ServiceSection
      v-else-if="activeStepCode === 'show-check-info' && inheritanceId"
      title="Информация о проверке"
      :selectedService="activeService"
      :show-action="true"
      :type="'show-service-response-viewer'"
      :selectedParticipant="selectedParticipant"
    >
      <template #action>
        <CheckInfoBlock
          :statementNotFound="statementNotFound"
          :existingCaseFound="existingCaseFound"
          :checkResults="checkResults"
          :isTestamentAuthor="isTestamentAuthor"
          @export-report="$emit('export-report')"
          @show-existing-case-info="$emit('show-existing-case-info')"
          @show-notary-data="$emit('show-notary-data')"
          @attach-testament="$emit('attach-testament')"
          @open-testament-document="$emit('open-testament-document')"
        />
      </template>
    </ServiceSection>

    <ServiceSection
      v-else
      :title="activeService ? activeService.name : 'Магические вставки'"
      :activeService="activeService"
      :selectedService="selectedService"
      :serviceResponseData="serviceResponseData"
      :searching="isSearching"
      :search-complected="searchCompleted"
      :show-action="true"
      :selectedParticipant="selectedParticipant"
      type="show-service-response-viewer"
      @insert:field="$emit('insert:field', $event)"
      @select:array-item="handleArrayItemSelected"
    >
      <template #action>
        <div
          v-if="serviceLoading"
          class="flex justify-center items-center py-8"
        >
          <Spinner size="sm" />
        </div>
        <div v-else-if="activeService">
          <div class="flex flex-col w-full">
            <div v-for="f in dynamicRequestFields" :key="f.key">
              <FormItem :float="f.type !== 'boolean'" class="pb-1">
                <FormLabel>
                  {{ f.title }}
                  <span class="text-destructive">*</span>
                </FormLabel>
                <component
                  :is="getFieldComponent(f)"
                  v-model="dynamicForm[f.key]"
                  :item="f"
                  :disabled="true"
                />
              </FormItem>
            </div>
          </div>

          <Button
            @click="getServiceResponse"
            class="w-full flex items-center justify-center"
            :disabled="isSearching"
            variant="outline"
          >
            <template v-if="isQueueRunning">
              <Spinner size="sm" class="mr-2" />
              {{ $t("В очереди...") }}
            </template>
            <template v-else-if="searching">
              <Spinner size="sm" class="mr-2" />
              {{ $t("Поиск...") }}
            </template>
            <template v-else>
              {{ $t("Запросить данные") }}
            </template>
          </Button>
        </div>
      </template>
    </ServiceSection>

    <PreviewTipTap2
      ref="previewRef"
      :template-lang="previewLang"
      :template-json="previewJson || {}"
    />
  </div>
</template>

<style scoped lang="scss">
.item {
  @apply min-h-9 px-4 outline outline-1 outline-border flex items-center justify-start cursor-pointer;
  border-radius: var(--radius);
}

.attorney-panel {
  @apply flex flex-col h-full bg-card  overflow-hidden;

  &__header {
    @apply flex flex-col items-start flex-shrink-0;
  }

  &__title {
    @apply text-lg font-semibold text-foreground;
  }

  &__body {
    @apply flex-1 min-h-0 overflow-y-auto py-4 flex flex-col;
  }

  &__footer {
    @apply flex items-center gap-3 flex-shrink-0;

    > * {
      @apply flex-1 min-w-0;
    }
  }

  &__inline-actions {
    @apply flex items-center gap-3 w-full max-w-md mx-auto;

    > * {
      @apply flex-1 min-w-0;
    }
  }
}

.attorney-loading {
  @apply flex-1 flex flex-col items-center justify-center text-center gap-4
    py-6;
  min-height: 280px;

  &__title {
    @apply text-base font-semibold text-foreground;
  }

  &__sub {
    @apply text-sm text-muted-foreground;
  }
}

.attorney-attached {
  @apply flex flex-col gap-3;

  &__title {
    @apply text-sm font-medium text-muted-foreground uppercase tracking-wide;
  }
}

.attorney-found {
  @apply flex flex-col gap-4;

  &__hint {
    @apply text-sm text-muted-foreground flex flex-wrap items-baseline gap-1;
  }

  &__iin {
    @apply font-medium text-foreground;
  }

  &__list {
    @apply flex flex-col gap-3;
  }
}

.attached-card {
  @apply relative p-5 flex flex-col gap-3 border border-border border-primary bg-muted;
  border-radius: var(--radius);

  &__icon {
    @apply w-5 h-5 shrink-0 text-green-600;
  }

  &__body {
    @apply flex flex-col gap-3 flex-1 min-w-0;
  }

  &__title {
    @apply flex items-center gap-2 text-sm font-semibold text-foreground;
  }

  &__rows {
    @apply flex flex-col gap-1.5;
  }
}

.attorney-card {
  @apply p-5 flex flex-col gap-3 border border-border bg-card
    transition-colors cursor-pointer;
  border-radius: var(--radius);

  &:hover {
    @apply border-primary/40 bg-accent/40;
  }

  &.is-selected {
    @apply border-primary bg-muted;
  }

  &__header {
    @apply flex items-start justify-between gap-3;
  }

  &__header-actions {
    @apply flex items-center gap-1.5 shrink-0;
  }

  &__title {
    @apply font-semibold text-foreground text-sm leading-5;
  }

  &__check {
    @apply text-primary w-4 h-4 shrink-0;
  }

  &__rows {
    @apply flex flex-col gap-1.5;
  }
}

.row {
  @apply flex flex-col gap-0.5 text-xs;

  &__label {
    @apply text-muted-foreground;
  }

  &__value {
    @apply text-foreground;
    word-break: break-word;

    &--status {
      @apply inline-flex items-center gap-1.5;
    }
  }
}

.attached-card .preview-btn {
  @apply absolute top-3 right-3;
}

.preview-btn {
  @apply inline-flex items-center justify-center w-7 h-7 rounded-full
    border border-border bg-card text-muted-foreground transition-colors
    cursor-pointer shrink-0;

  &:hover {
    @apply bg-accent text-foreground border-primary/40;
  }

  &:focus-visible {
    @apply outline-none ring-2 ring-primary/40;
  }

  &:disabled {
    @apply opacity-60 cursor-default;
  }
}
</style>
